import type { McpConnectionList } from '@easyhelp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ApiError, apiRequest } from './client';
import { teamQueryKey } from './team';

/**
 * Radice delle chiavi delle connessioni MCP: si invalidano tutte dopo una revoca, «Chiudi le
 * sessioni» e la rimozione dal Team (che le revocano sul server).
 */
export const mcpConnectionsQueryKey = ['mcp', 'connections'] as const;

/** Chiave delle proprie connessioni (profilo). */
export const ownMcpConnectionsQueryKey = [...mcpConnectionsQueryKey, 'me'] as const;

/** Chiave delle connessioni di un membro (Impostazioni › Team). */
export function memberMcpConnectionsQueryKey(memberId: string) {
  return [...mcpConnectionsQueryKey, 'member', memberId] as const;
}

/** `GET /me/mcp-connections`: le proprie connessioni MCP attive (PRD-007 US-2). */
export function useOwnMcpConnections() {
  return useQuery<McpConnectionList, ApiError>({
    queryKey: ownMcpConnectionsQueryKey,
    queryFn: () => apiRequest<McpConnectionList>('/me/mcp-connections'),
  });
}

/** `GET /team/members/:id/mcp-connections`: le connessioni MCP attive di un membro, per l'amministratore. */
export function useMemberMcpConnections(memberId: string) {
  return useQuery<McpConnectionList, ApiError>({
    queryKey: memberMcpConnectionsQueryKey(memberId),
    queryFn: () => apiRequest<McpConnectionList>(`/team/members/${memberId}/mcp-connections`),
  });
}

/**
 * Dopo una revoca (riuscita o no: un 404 vuol dire che l'elenco è vecchio) si rileggono gli elenchi;
 * se il membro non c'è più (`team.member_not_found`) anche il Team.
 */
function useRevokeMutation(path: (connectionId: string) => string) {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (connectionId) => apiRequest<void>(path(connectionId), { method: 'POST' }),
    onSettled: (_data, error) => {
      if (error?.code === 'team.member_not_found') void queryClient.invalidateQueries({ queryKey: teamQueryKey });
      return queryClient.invalidateQueries({ queryKey: mcpConnectionsQueryKey });
    },
  });
}

/** `POST /me/mcp-connections/:id/revoke`: revoca una propria connessione. */
export function useRevokeOwnMcpConnection() {
  return useRevokeMutation((connectionId) => `/me/mcp-connections/${connectionId}/revoke`);
}

/** `POST /team/members/:id/mcp-connections/:connectionId/revoke`: l'amministratore revoca la connessione di un membro. */
export function useRevokeMemberMcpConnection(memberId: string) {
  return useRevokeMutation((connectionId) => `/team/members/${memberId}/mcp-connections/${connectionId}/revoke`);
}
